import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../hooks/useApiCall';
import activitiesService from '../../../../../services/activitiesService';
import removeDuplicates from '../../../../../utils/removeDuplicates';
import splitArray from '../../../../../utils/splitArray';
import { ActivityRatingFromApi, GetAllActivitiesRatingsResponse, MappedActivityRating } from '../../types';

interface UseLoadRatingsInterface {
  setRatingsSplited: Dispatch<SetStateAction<Array<MappedActivityRating[]>>>;
  setFilteredRatings: Dispatch<SetStateAction<MappedActivityRating[]>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

export default function useLoadRatings({
  setRatingsSplited,
  setFilteredRatings,
  setCurrentPage,
}: UseLoadRatingsInterface) {
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [fullRatings, setFullRatings] = useState<MappedActivityRating[]>([]);
  const [doesListApiHasError, setDoesListApiHasError] = useState(false);
  const [activitiesList, setActivitiesList] = useState([{ value: '', label: 'Todas' }]);
  const [durationRateOptions, setDurationRateOptions] = useState([{ value: '', label: 'Todas' }]);

  const { apiCall } = useApiCall();

  // Opções de filtro de notas (1 a 5 estrelas)
  const teacherRateOptions = useMemo(() => [
    { value: '', label: 'Todas' },
    { value: '5', label: '⭐⭐⭐⭐⭐ (5/5)' },
    { value: '4', label: '⭐⭐⭐⭐ (4/5)' },
    { value: '3', label: '⭐⭐⭐ (3/5)' },
    { value: '2', label: '⭐⭐ (2/5)' },
    { value: '1', label: '⭐ (1/5)' },
  ], []);

  const contentRateOptions = useMemo(() => [
    { value: '', label: 'Todas' },
    { value: '5', label: '⭐⭐⭐⭐⭐ (5/5)' },
    { value: '4', label: '⭐⭐⭐⭐ (4/5)' },
    { value: '3', label: '⭐⭐⭐ (3/5)' },
    { value: '2', label: '⭐⭐ (2/5)' },
    { value: '1', label: '⭐ (1/5)' },
  ], []);

  const loadRatings = useCallback(async () => {
    await apiCall({
      apiToCall: activitiesService.getAllActivitiesRatings,
      onStartLoad: () => setIsLoadLoading(true),
      onEndLoad: () => setIsLoadLoading(false),
      actionAfterResponse: (apiResponse: GetAllActivitiesRatingsResponse) => {
        if (!apiResponse.success) {
          toast.error('Não foi possível carregar a lista de avaliações', {
            toastId: 'getRatingsFail',
          });
          setDoesListApiHasError(true);
          setIsLoadLoading(false);
          return;
        }

        const ratingsList: ActivityRatingFromApi[] = apiResponse.ratings;

        const mappedRatingsList = ratingsList?.map((rating, index) => ({
          id: `${rating.activityId}-${rating.partnerId}-${index}`,
          ratedAt: rating.ratedAt,
          activityId: rating.activityId,
          activity: rating.activity,
          partnerId: rating.partnerId,
          partner: rating.partner,
          teacherRate: rating.teacherRate,
          contentRate: rating.contentRate,
          durationRate: rating.durationRate,
          comment: rating.comment || '',
        }));

        // Criar lista de atividades únicas
        const activities = removeDuplicates(mappedRatingsList.map((rating) => rating.activity));
        const activitiesMapped = activities.map((activity: string) => ({ value: activity, label: activity }));
        activitiesMapped.unshift({ value: '', label: 'Todas' });
        setActivitiesList(activitiesMapped);

        // Criar lista de durações únicas
        const durations = removeDuplicates(mappedRatingsList.map((rating) => rating.durationRate).filter(Boolean));
        const durationsMapped = durations.map((duration: string) => ({ value: duration, label: duration }));
        durationsMapped.unshift({ value: '', label: 'Todas' });
        setDurationRateOptions(durationsMapped);

        setFullRatings(mappedRatingsList);

        const splitedArray = splitArray(mappedRatingsList);
        setRatingsSplited(splitedArray);
        setFilteredRatings(splitedArray[0]);
        setCurrentPage(0);
        setDoesListApiHasError(false);
      },
      catchAction: () => setDoesListApiHasError(true),
      catchMessage: 'Não foi possível carregar a lista de avaliações',
    });
  }, [apiCall, setCurrentPage, setFilteredRatings, setRatingsSplited]);

  useEffect(() => {
    loadRatings();
  }, [loadRatings]);

  return {
    isLoadLoading,
    fullRatings,
    doesListApiHasError,
    activitiesList,
    teacherRateOptions,
    contentRateOptions,
    durationRateOptions,
    loadRatings,
  };
}
